/**
 * Who is working this record.
 *
 * A checkbox list rather than a multi-select: it needs no combobox dependency,
 * works on touch, and is legible with a keyboard. The whole set posts at once,
 * because the endpoint replaces the set rather than toggling one member.
 *
 * Distinct from the owner picker — ownership is single and commercial, assignment
 * is many and operational.
 */

import { useFetcher } from 'react-router-dom'
import type { Assignment, SubjectRef } from '@erp/contracts'
import { useUsers } from '../status-context'
import { Button } from '../ui'

export function AssigneePicker({
  subject,
  assignments,
}: {
  subject: SubjectRef
  assignments: readonly Assignment[]
}) {
  const users = useUsers()
  const fetcher = useFetcher()

  const assigned = new Set(assignments.map((a) => a.userId))

  // Inactive people stay listed while still assigned, so a record does not
  // silently lose its assignee when someone leaves — but they cannot be added.
  const selectable = users.filter((user) => user.active || assigned.has(user.id))

  const pending = fetcher.formData
  const submitting = fetcher.state !== 'idle'

  return (
    <fetcher.Form method="post" className="ax-assignees">
      <input type="hidden" name="intent" value="assign" />
      <input type="hidden" name="subjectType" value={subject.type} />
      <input type="hidden" name="subjectId" value={subject.id} />

      <span className="ax-field-label">Assigned to</span>

      {selectable.length === 0 ? (
        <p className="ax-text-muted">No one available to assign.</p>
      ) : (
        <ul className="ax-assignee-list">
          {selectable.map((user) => {
            // Optimistic: reflect the submitted set rather than the stale one.
            const checked = pending
              ? pending.getAll('userId').includes(user.id)
              : assigned.has(user.id)

            return (
              <li key={user.id}>
                <label className="ax-checkbox">
                  <input type="checkbox" name="userId" value={user.id} defaultChecked={checked} />
                  {user.active ? user.name : `${user.name} (inactive)`}
                </label>
              </li>
            )
          })}
        </ul>
      )}

      <Button type="submit" disabled={submitting}>
        {submitting ? 'Saving…' : 'Update assignees'}
      </Button>
    </fetcher.Form>
  )
}

/** Read-only summary, for list rows and headers. */
export function AssigneeNames({ assignments }: { assignments: readonly Assignment[] }) {
  const users = useUsers()
  if (assignments.length === 0) return <span className="ax-text-muted">Unassigned</span>

  const names = assignments.map(
    (a) => users.find((user) => user.id === a.userId)?.name ?? 'Unknown',
  )
  return <span className="ax-assignee-names">{names.join(', ')}</span>
}
